import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Newspaper, Calendar, ArrowRight } from 'lucide-react';

const NewsPage = () => {
  const newsItems = [
    {
      id: 'uk-gambling-regulations',
      title: 'New UK Gambling Regulations Take Effect: What Players Need to Know',
      date: '29.11.2025',
      excerpt: 'Recent updates to UK gambling laws introduce enhanced player protections and stricter operator requirements. Here\'s how these changes affect your gaming experience.',
      image: '/images/esteban-lopez-UIvRqbmtlFU-unsplash.webp',
    },
    {
      id: 'top-slot-games',
      title: 'Top 5 Slot Games Dominating UK Casinos This Month',
      date: '25.11.2025',
      excerpt: 'From classic fruit machines to modern video slots, discover which games are paying out the most and attracting the biggest player bases across UK platforms.',
      image: '/images/krissia-cruz-FwPzGn59wNs-unsplash.webp',
    },
    {
      id: 'cryptocurrency-payments',
      title: 'Cryptocurrency Payments Gain Traction in UK Online Casinos',
      date: '20.11.2025',
      excerpt: 'More UK casinos are embracing crypto deposits and withdrawals. We break down which operators offer Bitcoin, Ethereum, and other digital currencies, plus the pros and cons.',
      image: '/images/lee-thomas-BjIALEkr_Wg-unsplash.webp',
    },
    {
      id: 'live-casino-trends',
      title: 'Live Dealer Games See Record Growth in UK Market',
      date: '15.11.2025',
      excerpt: 'Live casino experiences are transforming how UK players engage with table games. Discover which operators offer the best live dealer experiences and what makes them stand out.',
      image: '/images/leo_visions-lscWuEllNoM-unsplash.webp',
    },
    {
      id: 'bonus-transparency',
      title: 'UK Casinos Improve Bonus Transparency Following Industry Pressure',
      date: '10.11.2025',
      excerpt: 'Major operators are simplifying bonus terms and making wagering requirements clearer. Learn how these changes benefit players and what to look for in transparent offers.',
      image: '/images/linoleum-creative-collective-dEvD01W9sg8-unsplash.webp',
    },
    {
      id: 'mobile-gaming-growth',
      title: 'Mobile Casino Gaming Reaches All-Time High in UK',
      date: '05.11.2025',
      excerpt: 'More players than ever are accessing casinos via smartphones and tablets. We explore the mobile gaming revolution and which casinos offer the best mobile experiences.',
      image: '/images/nemo-KAG7Zm4tXew-unsplash.webp',
    },
    {
      id: 'responsible-gambling-tools',
      title: 'New Responsible Gambling Tools Launch Across UK Platforms',
      date: '01.11.2025',
      excerpt: 'Enhanced deposit limits, reality checks, and self-exclusion features are becoming standard. Here\'s how these tools help players maintain control and what\'s coming next.',
      image: '/images/point-normal-44cKaUuRmBg-unsplash.webp',
    },
    {
      id: 'payment-speed-improvements',
      title: 'Withdrawal Times Improve as Casinos Compete on Speed',
      date: '28.10.2025',
      excerpt: 'Faster payouts are becoming a key differentiator in the UK market. Discover which casinos process withdrawals fastest and how payment methods affect speed.',
      image: '/images/samuel-branch-YeEx_N0QcxQ-unsplash.webp',
    },
    {
      id: 'game-provider-partnerships',
      title: 'Exclusive Game Provider Partnerships Reshape UK Casino Landscape',
      date: '22.10.2025',
      excerpt: 'Casinos are securing exclusive deals with top developers, creating unique game libraries. Find out which operators offer games you can\'t find elsewhere.',
      image: '/images/sophia-muller-q5uLzR1A_-8-unsplash.webp',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-dark to-gray-medium text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Newspaper className="text-yellow-accent" size={40} />
              <h1 className="text-4xl md:text-5xl font-bold">Casino News</h1>
            </div>
            <p className="text-xl text-gray-200 leading-relaxed">
              Stay informed about the latest developments, trends, and changes in the UK online casino industry.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-dark text-center mb-12">
            Latest Casino News
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {newsItems.map((news, index) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                data-discover="true"
                className={`bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-yellow-accent hover:shadow-lg transition-all transform hover:scale-105 ${
                  index === 0 ? 'border-yellow-accent' : ''
                }`}
              >
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${news.image})` }}
                ></div>
                
                <div className="p-6">
                  <div className="flex items-center space-x-2 text-sm text-gray-light mb-3">
                    <Calendar size={16} />
                    <span>{news.date}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-dark mb-3 leading-tight">
                    {news.title}
                  </h3>
                  
                  <p className="text-gray-light mb-4 leading-relaxed">
                    {news.excerpt}
                  </p>
                  
                  <div className="inline-flex items-center space-x-2 text-yellow-accent font-semibold hover:text-yellow-light transition-colors">
                    <span>Read More</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-dark mb-6">How We Choose Our News</h2>
            
            <div className="prose prose-lg max-w-none text-gray-light leading-relaxed space-y-6">
              <p>
                The UK online casino industry moves fast. New regulations emerge, operators launch 
                innovative features, payment methods evolve, and player preferences shift constantly. 
                Our news selection process ensures you receive information that matters – stories 
                that impact your gaming experience, influence your decisions, and help you stay 
                ahead of industry changes.
              </p>
              
              <p>
                We prioritize news that directly affects players. Regulatory changes top our list 
                because they impact everything from bonus terms to withdrawal processes. When the 
                UK Gambling Commission introduces new rules, we analyze how they affect your 
                gameplay, your money, and your rights. We don't just report what changed – we 
                explain what it means for you.
              </p>
              
              <p>
                Industry trends matter because they reveal where the market is heading. When 
                multiple casinos adopt new payment methods, improve withdrawal speeds, or enhance 
                mobile experiences, these aren't isolated events – they're signals of broader 
                shifts. We track these patterns and report on developments that indicate 
                meaningful changes in how casinos operate.
              </p>
              
              <p>
                Player impact guides every story we cover. A new game launch might seem exciting, 
                but if it doesn't offer genuine value or innovation, we skip it. Similarly, 
                operator announcements about partnerships or expansions only make our news if they 
                translate to better experiences for players. We filter out marketing noise and 
                focus on substance.
              </p>
              
              <p>
                Verification is non-negotiable. Before publishing any news, we verify facts through 
                multiple sources – official regulatory announcements, operator statements, and 
                industry reports. We don't rely on rumors or unconfirmed reports. If we can't 
                verify information, we don't publish it. Your trust depends on our accuracy.
              </p>
              
              <p>
                Timeliness matters, but accuracy matters more. We'd rather publish verified news 
                slightly later than rush unconfirmed stories. However, when major developments 
                occur – regulatory changes, significant operator updates, or industry-shifting 
                announcements – we prioritize rapid but accurate reporting.
              </p>
              
              <p>
                We balance breaking news with analysis. Reporting that something happened is only 
                half the story – explaining why it matters and how it affects players completes 
                the picture. Our news articles include context, implications, and actionable 
                insights that help you understand developments beyond surface-level reporting.
              </p>
              
              <p>
                Our editorial independence ensures unbiased coverage. We don't favor specific 
                operators or suppress negative news to maintain relationships. If an operator 
                faces regulatory action, we report it. If industry practices harm players, we 
                cover it. Our commitment is to you, not to operators or advertisers.
              </p>
              
              <p>
                We focus on UK-specific developments because that's what matters most to our 
                readers. International casino news might be interesting, but if it doesn't affect 
                UK players or operators, it doesn't make our pages. This focus ensures every 
                story we publish has direct relevance to your gaming experience.
              </p>
              
              <p>
                Finally, we prioritize stories that help you make better decisions. News about 
                improved withdrawal speeds helps you choose faster-paying casinos. Reports on 
                regulatory changes help you understand your rights. Coverage of new payment 
                methods helps you select convenient options. Every story serves a purpose beyond 
                simple information – it empowers smarter choices.
              </p>
              
              <p>
                Our news selection isn't arbitrary – it's strategic, verified, and player-focused. 
                We curate information that matters, verify facts rigorously, and present stories 
                with context that helps you understand implications. In an industry full of 
                marketing messages and promotional content, we provide news that genuinely serves 
                your interests.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;

